import { useEffect, useState } from 'react';

/**
 * A hook for handling keyboard navigation in lists
 *
 * @example
 * ```tsx
 * const MyList = () => {
 *   const items = ['Item 1', 'Item 2', 'Item 3'];
 *   const {
 *     selectedIndex,
 *     handleKeyDown,
 *     setSelectedIndex
 *   } = useKeyboardList({
 *     items,
 *     onSelect: (item) => console.log('Selected:', item)
 *   });
 *
 *   return (
 *     <div onKeyDown={handleKeyDown}>
 *       {items.map((item, index) => (
 *         <div
 *           key={index}
 *           className={index === selectedIndex ? 'selected' : ''}
 *           onClick={() => setSelectedIndex(index)}
 *         >
 *           {item}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * };
 */
export interface UseKeyboardListProps<T> {
  /** The list of items to navigate through */
  items: T[];
  /** Callback when an item is selected (e.g. when Enter is pressed) */
  onSelect?: (item: T) => void;
  /** Initial selected index */
  initialIndex?: number;
  /** Whether to enable circular navigation (going from last to first item and vice versa) */
  circular?: boolean;
  /** Indexes to skip during navigation (e.g. dividers) */
  skipIndexes?: number[];
}

export interface UseKeyboardListReturn {
  /** The currently selected index */
  selectedIndex: number;
  /** Function to handle keyboard events */
  handleKeyDown: (event: KeyboardEvent) => boolean;
  /** Function to manually set the selected index */
  setSelectedIndex: (index: number) => void;
}

const findNextValidIndex = (
  currentIndex: number,
  direction: 1 | -1,
  maxIndex: number,
  skipIndexes: Set<number>,
  circular: boolean
): number => {
  let nextIndex = currentIndex;
  do {
    nextIndex = nextIndex + direction;
    if (circular) {
      nextIndex = (nextIndex + maxIndex + 1) % (maxIndex + 1);
    } else {
      if (nextIndex < 0) return 0;
      if (nextIndex > maxIndex) return maxIndex;
    }
  } while (skipIndexes.has(nextIndex) && nextIndex !== currentIndex);

  return nextIndex;
};

export function useKeyboardList<T>({
  items,
  onSelect,
  initialIndex = 0,
  circular = true,
  skipIndexes = [],
}: UseKeyboardListProps<T>): UseKeyboardListReturn {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const skipIndexSet = new Set(skipIndexes);

  // Ensure selectedIndex stays within bounds when items change
  useEffect(() => {
    const maxIndex = items.length - 1;
    if (maxIndex >= 0 && selectedIndex > maxIndex) {
      setSelectedIndex(maxIndex);
    }
  }, [items.length, selectedIndex]);

  const handleKeyDown = (event: KeyboardEvent): boolean => {
    const maxIndex = items.length - 1;

    switch (event.key) {
      case 'ArrowUp':
      case 'Up': {
        event.preventDefault();
        event.stopPropagation();
        const nextIndex = findNextValidIndex(selectedIndex, -1, maxIndex, skipIndexSet, circular);
        setSelectedIndex(nextIndex);
        return true;
      }

      case 'ArrowDown':
      case 'Down': {
        event.preventDefault();
        event.stopPropagation();
        const nextIndex = findNextValidIndex(selectedIndex, 1, maxIndex, skipIndexSet, circular);
        setSelectedIndex(nextIndex);
        return true;
      }

      case 'Enter': {
        event.preventDefault();
        const selectedItem = items[selectedIndex];
        if (selectedItem !== undefined && !skipIndexSet.has(selectedIndex)) {
          onSelect?.(selectedItem);
        }
        return true;
      }

      default:
        return false;
    }
  };

  return {
    selectedIndex,
    handleKeyDown,
    setSelectedIndex,
  };
}
